// App.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Box } from '@mui/material';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './pages/shared/Login';
import UserRegistration from './pages/admin/UserRegistration';
import UserList from './pages/admin/UserList';
import UserEdit from './pages/admin/UserEdit';
import UserDetails from './pages/admin/UserDetails';
import HorseRegistration from './pages/admin/HorseRegistration';
import HorseList from './pages/admin/HorseList';
import HorseEdit from './pages/admin/HorseEdit';
import HorseCompetitionsPage from './pages/admin/HorseCompetitionsPage';
import Statistics from './pages/admin/Statistics';
import SystemSettings from './pages/admin/SystemSettings';
import AccountantReview from './pages/admin/AccountantReview';
import HorseDetails from './pages/shared/HorseDetails';
import HorseDetailsClient from './pages/shared/HorseDetailsClient';
import ClientHorseDetails from './pages/shared/ClientHorseDetails';
import HorseProductionTimeline from './pages/shared/HorseProductionTimeline';
import ClientHorseList from './pages/shared/ClientHorseList';
import ClientHorseStats from './pages/shared/ClientHorseStats';
import PedigreeTree from './pages/shared/PedigreeTree';
import OwnerList from './pages/admin/OwnerList';
import OwnerHorsesList from './pages/admin/OwnerHorsesList';
import OwnerEdit from './pages/admin/OwnerEdit';
import OwnerRegistration from './pages/admin/OwnerRegistration';
import OwnerDetails from './pages/admin/OwnerDetails';
import StableList from './pages/admin/StableList';
import StableRoomList from './pages/admin/StableRoomList';
import AccommodationList from './pages/admin/AccommodationList';
import AvailableRoomsView from './pages/admin/AvailableRoomsView';
import HorseColorList from './pages/admin/HorseColorList';
import HorseBreedList from './pages/admin/HorseBreedList';
import HorseTransportForm from './pages/admin/HorseTransportForm';
import HorseTransportReportPage from './pages/admin/HorseTransportReportPage';
import ProceduresList from './pages/clinic/ProceduresList';
import RecordList from './pages/clinic/RecordList';
import MedicationsList from './pages/clinic/MedicationsList';
import ProcedureAdd from './pages/clinic/ProcedureAdd';
import MedicationAdd from './pages/clinic/MedicationAdd';
import HorseHistory from './pages/clinic/HorseHistory';
import MedicationsEdit from './pages/clinic/MedicationsEdit';
import VetClinicRecordInside from './pages/clinic/VetClinicRecordInside';
import ClinicApprovals from './pages/clinic/ClinicApprovals';
import StallionForm from './pages/clinic/StallionForm';
import SemenInventory from './pages/clinic/SemenInventory';
import VetClinicRecordEdit from './pages/clinic/VetClinicRecordEdit';
import ReproductionProceduresList from './pages/clinic/ReproductionProceduresList';
import FarrierDetails from './pages/clinic/FarrierDetails';
import RecordDetails from './pages/clinic/RecordDetails';
import ProcedureEdit from './pages/clinic/ProcedureEdit';
import EditFarrierRecord from './pages/clinic/EditFarrierRecord';
import AddFarrierRecordInside from './pages/clinic/AddFarrierRecordInside';
import FarrierList from './pages/clinic/FarrierList';
import FarrierProcedureAdd from './pages/clinic/FarrierProcedureAdd';
import FarrierProceduresList from './pages/clinic/FarrierProceduresList';
import FarrierProcedureEdit from './pages/clinic/FarrierProcedureEdit';
import FarrierMaterialsList from './pages/clinic/FarrierMaterialsList';
import FarrierMaterialAdd from './pages/clinic/FarrierMaterialAdd';
import FarrierMaterialsEdit from './pages/clinic/FarrierMaterialsEdit';
import ReproductionPage from './pages/clinic/ReproductionPage';
import EditReproductionProcedure from './pages/clinic/EditReproductionProcedure';
import AddReproductionProcedure from './pages/clinic/AddReproductionProcedure';
import ChangePassword from './pages/shared/ChangePassword';
import NotFoundPage from './pages/shared/NotFoundPage';
import UserProfile from './pages/Profile';
import PrivateRoute from './components/PrivateRoute';
import Settings from './pages/admin/Settings';
import NoAccessPage from './pages/shared/NoAccessPage';
import RequirePermission from './components/RequirePermission';
import AccessControlCenter from './pages/admin/AccessControlCenter';
import AuditReportPage from './pages/admin/AuditReportPage';
import DatabaseBackup from './pages/admin/DatabaseBackup';
import ServicePricing from './pages/admin/ServicePricing';
import RestoreDeletedHorses from './pages/admin/RestoreDeletedHorses';
import CheckupTypesPage from './pages/clinic/CheckupTypesPage';
import HorseNFCPage from './pages/shared/HorseNFCPage';
import VetCheckupView from './pages/clinic/VetCheckupView';
import VetCheckupReport from './pages/clinic/VetCheckupReport';
import OwnerAlertTemplates from './pages/admin/OwnerAlertTemplates';
import { RouteGuardOutlet } from './routers/RouteGuard';
import FinanceSummaryPage from './pages/accounting/FinanceSummaryPage';
import FinanceEntitySummaryPage from './pages/accounting/FinanceEntitySummaryPage';
import HorseLedgerInsidePage from './pages/accounting/HorseLedgerInsidePage';
// Lazy loading for better performance
import './App.css';

// Lazy loading for better performance
const Home = lazy(() => import('./pages/shared/Home'));
const Profile = lazy(() => import('./pages/Profile'));
// Lazy load other components similarly
/*function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const toggleSidebar = () => setSidebarExpanded((prev) => !prev);

  return (
    <Router>
      <AppLayout
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        sidebarExpanded={sidebarExpanded}
        toggleSidebar={toggleSidebar}
      />
    </Router>
  );
}
*/
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const toggleSidebar = () => setSidebarExpanded((prev) => !prev);

  // Build a Data Router that routes everything to AppLayout.
  // This lets us enable React Router v7 future flags without refactoring your inner <Routes>.
  const router = React.useMemo(
    () =>
      createBrowserRouter(
        [
          {
            path: '/*', // send all paths to AppLayout which already contains <Routes>
            element: (
              <AppLayout
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                sidebarExpanded={sidebarExpanded}
                toggleSidebar={toggleSidebar}
              />
            ),
          },
        ],
        {
          // Opt-in to v7 behavior to silence warnings
          future: {
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          },
        }
      ),
    [isAuthenticated, sidebarExpanded]
  );

  return (
    <RouterProvider
      router={router}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    />
  );
}

function AppLayout({
  isAuthenticated,
  setIsAuthenticated,
  sidebarExpanded,
  toggleSidebar,
}) {
  const location = useLocation(); // ✅ هذا سيعمل الآن بشكل صحيح
  const { i18n } = useTranslation();
  const showAppChrome = isAuthenticated && location.pathname !== '/login';
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.body.dir = dir;
    document.documentElement.lang = i18n.language || 'en';
  }, [dir, i18n.language]);

  return (
    <Box
      className={sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}
      sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}
    >
      {/* Header, Sidebar */}
      {showAppChrome && (
        <>
          <Header
            sidebarExpanded={sidebarExpanded}
            toggleSidebar={toggleSidebar}
            setIsAuthenticated={setIsAuthenticated}
          />
          <Sidebar
            expanded={sidebarExpanded}
            toggleExpand={toggleSidebar}
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticated}
          />
        </>
      )}

      {/* Main Content and Routes */}
      <Box
        className="app-main-content"
        dir={dir}
        sx={{
          flex: 1,
          padding: showAppChrome ? { xs: '16px', md: '20px' } : 0,
          marginLeft: showAppChrome ? (sidebarExpanded ? '250px' : '85px') : 0,
          boxSizing: 'border-box',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Route تسجيل الدخول */}
            <Route
              path="/login"
              element={<Login setIsAuthenticated={setIsAuthenticated} />}
            />

            {/* توجيه غير المسموحين إلى صفحة تسجيل الدخول */}
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? '/home' : '/login'} />}
            />

            {/* عرض صفحة "غير موجود" لجميع الروابط غير الصحيحة */}
            <Route path="*" element={<NotFoundPage />} />

            {/* Private Routes */}
            <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
              <Route path="/No-access" element={<NoAccessPage />} />
              {/* Gate every private route by menu/permissions */}
              <Route element={<RouteGuardOutlet />}>
                <Route
                  path="/home"
                  element={
                    <RequirePermission page="home">
                      <Home />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/profile"
                  element={<Profile />}
                />
                <Route
                  path="/ChangePassword"
                  element={<ChangePassword />}
                />
                <Route
                  path="/register-horse"
                  element={
                    <RequirePermission page="register-horse">
                      <HorseRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-list"
                  element={
                    <RequirePermission page="horse-list">
                      <HorseList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/competitions"
                  element={
                    <RequirePermission page="competitions">
                      <HorseCompetitionsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-edit/:horseId"
                  element={
                    <RequirePermission page="horse-edit">
                      <HorseEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-details/:horseId"
                  element={
                    <RequirePermission page="horse-details">
                      <HorseDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-details/:horseId/production-timeline"
                  element={
                    <RequirePermission page="horse-details">
                      <HorseProductionTimeline />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-details-client/:horseId"
                  element={
                    <RequirePermission page="horse-details-client">
                      <HorseDetailsClient />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/my-horses"
                  element={
                    <RequirePermission page="my-horses">
                      <ClientHorseList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/my-horses/:horseId"
                  element={
                    <RequirePermission page="my-horse-details" anyOf={['my-horses']}>
                      <ClientHorseDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/my-horse-stats"
                  element={
                    <RequirePermission page="my-horse-stats">
                      <ClientHorseStats />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-colors"
                  element={
                    <RequirePermission page="horse-colors">
                      <HorseColorList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse-breeds"
                  element={
                    <RequirePermission page="horse-breeds">
                      <HorseBreedList />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/CheckupTypesPage"
                  element={
                    <RequirePermission page="CheckupTypesPage">
                      <CheckupTypesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/RestoreDeletedHorses"
                  element={
                    <RequirePermission page="RestoreDeletedHorses">
                      <RestoreDeletedHorses />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/Statistics"
                  element={
                    <RequirePermission page="Statistics">
                      <Statistics />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/SystemSettings"
                  element={
                    <RequirePermission page="SystemSettings">
                      <SystemSettings />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/OwnerAlertTemplates"
                  element={
                    <RequirePermission page="OwnerAlertTemplates">
                      <OwnerAlertTemplates />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/AccountantReview"
                  element={
                    <RequirePermission page="AccountantReview">
                      <AccountantReview />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/HorseTransportForm"
                  element={
                    <RequirePermission page="HorseTransportForm">
                      <HorseTransportForm />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/HorseTransportReportPage"
                  element={
                    <RequirePermission page="HorseTransportReportPage">
                      <HorseTransportReportPage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/PedigreeTree"
                  element={
                    <RequirePermission page="PedigreeTree">
                      <PedigreeTree />
                    </RequirePermission>
                  }
                />
                {/* Owners Management */}
                <Route
                  path="/owner-list"
                  element={
                    <RequirePermission page="owner-list">
                      <OwnerList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/OwnerHorsesList/:ownerId"
                  element={
                    <RequirePermission page="OwnerHorsesList">
                      <OwnerHorsesList />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/owner-edit/:ownerId"
                  element={
                    <RequirePermission page="owner-edit">
                      <OwnerEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/register-owner"
                  element={
                    <RequirePermission page="register-owner">
                      <OwnerRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/owner-details/:ownerId"
                  element={
                    <RequirePermission page="owner-details">
                      <OwnerDetails />
                    </RequirePermission>
                  }
                />
                {/* Stable and Rooms Management */}
                <Route
                  path="/stable-list"
                  element={
                    <RequirePermission page="stable-list">
                      <StableList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/stable-rooms/:stableId"
                  element={
                    <RequirePermission page="stable-rooms">
                      <StableRoomList />
                    </RequirePermission>
                  }
                />
                {/* Accommodations */}
                <Route
                  path="/accommodations"
                  element={
                    <RequirePermission page="accommodations">
                      <AccommodationList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/available-rooms"
                  element={
                    <RequirePermission page="available-rooms">
                      <AvailableRoomsView />
                    </RequirePermission>
                  }
                ></Route>
                <Route
                  path="/stable-room-list"
                  element={
                    <RequirePermission page="stable-room-list">
                      <StableRoomList />
                    </RequirePermission>
                  }
                />
                {/* Users Management */}
                <Route
                  path="/userprofile"
                  element={
                    <RequirePermission page="userprofile">
                      <UserProfile />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/user-list"
                  element={
                    <RequirePermission page="user-list">
                      <UserList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/userregistration"
                  element={
                    <RequirePermission page="userregistration">
                      <UserRegistration />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/user-details/:userId"
                  element={
                    <RequirePermission page="user-details">
                      <UserDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/useredit/:userId"
                  element={
                    <RequirePermission page="useredit">
                      <UserEdit />
                    </RequirePermission>
                  }
                />
                {/* Veterinary Clinic */}
                <Route
                  path="/VetClinicForm"
                  element={
                    <RequirePermission page="vetclinicform">
                      <VetClinicRecordInside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/procedures"
                  element={
                    <RequirePermission page="procedures">
                      <ProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/procedure-add"
                  element={
                    <RequirePermission page="procedure-add">
                      <ProcedureAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ProcedureEdit/:procedureId"
                  element={
                    <RequirePermission page="ProcedureEdit">
                      <ProcedureEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medications"
                  element={
                    <RequirePermission page="medications">
                      <MedicationsList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medication-add"
                  element={
                    <RequirePermission page="medication-add">
                      <MedicationAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/medication-edit/:medicationId"
                  element={
                    <RequirePermission page="medication-edit">
                      <MedicationsEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/StallionForm"
                  element={
                    <RequirePermission page="StallionForm">
                      <StallionForm />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/semen-inventory"
                  element={
                    <RequirePermission page="semen-inventory">
                      <SemenInventory />
                    </RequirePermission>
                  }
                />
                {/* Records and Farrier Management */}
                <Route
                  path="/RecordList"
                  element={
                    <RequirePermission page="RecordList">
                      <RecordList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/record-details/:record_id"
                  element={
                    <RequirePermission page="record-details">
                      <RecordDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/AddFarrierRecord"
                  element={
                    <RequirePermission page="addfarrierrecord">
                      <AddFarrierRecordInside />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/EditFarrierRecord/:record_id"
                  element={
                    <RequirePermission page="EditFarrierRecord">
                      <EditFarrierRecord />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/FarrierList"
                  element={
                    <RequirePermission page="FarrierList">
                      <FarrierList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/horse/:id"
                  element={
                    <RequirePermission page="horse-nfc">
                      <HorseNFCPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/VetCheckupView"
                  element={
                    <RequirePermission page="vetcheckupview">
                      <VetCheckupView />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/VetCheckupReport"
                  element={
                    <RequirePermission page="vetcheckupreport">
                      <VetCheckupReport />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/VetClinicRecordEdit/:record_id"
                  element={
                    <RequirePermission page="VetClinicRecordEdit">
                      <VetClinicRecordEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/FarrierDetails/:record_id"
                  element={
                    <RequirePermission page="FarrierDetails">
                      <FarrierDetails />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedure-add"
                  element={
                    <RequirePermission page="farrier-procedure-add">
                      <FarrierProcedureAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedures"
                  element={
                    <RequirePermission page="farrier-procedures">
                      <FarrierProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-procedure-edit/:procedureId"
                  element={
                    <RequirePermission page="farrier-procedure-edit">
                      <FarrierProcedureEdit />
                    </RequirePermission>
                  }
                />
                <Route path="/farrier-medications" element={<Navigate to="/farrier-materials" replace />} />
                <Route path="/farrier-medication-add" element={<Navigate to="/farrier-material-add" replace />} />
                <Route path="/farrier-medication-edit/:medicationId" element={<Navigate to="/farrier-materials" replace />} />
                <Route
                  path="/farrier-materials"
                  element={
                    <RequirePermission page="farrier-materials">
                      <FarrierMaterialsList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-material-add"
                  element={
                    <RequirePermission page="farrier-material-add">
                      <FarrierMaterialAdd />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/farrier-material-edit/:medicationId"
                  element={
                    <RequirePermission page="farrier-material-edit">
                      <FarrierMaterialsEdit />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/HorseHistory/:horseId"
                  element={
                    <RequirePermission page="HorseHistory">
                      <HorseHistory />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ClinicApprovals"
                  element={
                    <RequirePermission page="ClinicApprovals">
                      <ClinicApprovals />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ReproductionPage"
                  element={
                    <RequirePermission page="ReproductionPage">
                      <ReproductionPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ReproductionProceduresList"
                  element={
                    <RequirePermission page="ReproductionProceduresList">
                      <ReproductionProceduresList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/EditReproductionProcedure/:id"
                  element={
                    <RequirePermission page="EditReproductionProcedure">
                      <EditReproductionProcedure />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/financesummary"
                  element={
                    <RequirePermission page="/financesummary">
                      <FinanceSummaryPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/finance-entity-summary"
                  element={
                    <RequirePermission page="/finance-entity-summary">
                      <FinanceEntitySummaryPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/HorseLedgerInsidePage"
                  element={
                    <RequirePermission page="/HorseLedgerInsidePage">
                      <HorseLedgerInsidePage />
                    </RequirePermission>
                  }
                />

                <Route
                  path="/AddReproductionProcedure"
                  element={
                    <RequirePermission page="AddReproductionProcedure">
                      <AddReproductionProcedure />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequirePermission page="settings">
                      <Settings />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings/access-control"
                  element={
                    <RequirePermission page="access-control">
                      <AccessControlCenter />
                    </RequirePermission>
                  }
                />
                <Route path="/access-control" element={<Navigate to="/settings/access-control" replace />} />
                <Route
                  path="/settings/audit-report"
                  element={
                    <RequirePermission page="settings/audit-report">
                      <AuditReportPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings/backups"
                  element={
                    <RequirePermission page="settings/backups">
                      <DatabaseBackup />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings/pricing"
                  element={
                    <RequirePermission page="settings/pricing">
                      <ServicePricing />
                    </RequirePermission>
                  }
                />
                {/* Add other protected routes here */}
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </Box>

      {/* Footer */}
      {isAuthenticated && location.pathname !== '/login' && (
        <Footer sidebarExpanded={sidebarExpanded} />
      )}
    </Box>
  );
}

export default App;
